import { makeAutoObservable, runInAction } from 'mobx';
import { Form } from 'mobx-react-form';
import { Match } from 'navigo';

import { AuthStore } from 'stores/users/AuthStore';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { HttpException } from 'utils/types';

export class UnderwritingPageStore {
  id: string = '';
  underwritingData?: UnderwritingOutput;
  loadStatus: 'loading' | 'error' | 'loaded' = 'loading';
  shouldShowConfirmationModalOnPageLeave: boolean = false;
  form?: Form = undefined;

  constructor(private readonly authStore: AuthStore) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    const id = match?.data?.id;
    if (id && id !== this.id) {
      this.loadUnderwriting(id);
    }
  }

  async loadUnderwriting(id: string) {
    this.loadStatus = 'loading';
    this.id = id;
    this.underwritingData = undefined;

    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/underwriting/${this.id}`);

      if (!response.ok) {
        // Sometimes this causes an error ?
        const exception: HttpException = nullsToUndefined(await response.json());
        console.error(exception);
        runInAction(() => {
          this.loadStatus = 'error';
        });
        return;
      }

      const data: UnderwritingOutput = nullsToUndefined(await response.json());

      runInAction(() => {
        this.loadStatus = 'loaded';
        this.underwritingData = data;
      });
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.loadStatus = 'error';
        this.underwritingData = undefined;
      });
    }
  }

  async saveUnderwriting(data: Partial<UnderwritingOutput>) {
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/underwriting/${encodeURIComponent(this.id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const exception: HttpException = nullsToUndefined(await response.json());
        throw new Error(exception.message);
      }

      const updatedData: UnderwritingOutput = nullsToUndefined(await response.json());
      runInAction(() => {
        this.underwritingData = updatedData;
      });
      return updatedData;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  setForm(form?: Form) {
    this.form = form;
  }

  showConfirmModal() {
    this.shouldShowConfirmationModalOnPageLeave = true;
  }

  hideConfirmationModal() {
    this.shouldShowConfirmationModalOnPageLeave = false;
  }

  destroy() {
    this.underwritingData = undefined;
  }
}
